import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import { IconCheck, IconCopy } from '@tabler/icons-react';

import { IconButton, Tooltip, Typography } from '@zendesk/forethought-elements';

// An identifier a support engineer pastes into a ticket or a logs query, so it
// is monospaced and copyable rather than selectable-by-hand.
export const CopyableId = ({
  ariaLabel,
  color,
  value,
}: {
  ariaLabel: string;
  color?: string;
  value: string;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();
  const [isCopied, setIsCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 1600);
    } catch {
      setIsCopied(false);
    }
  };

  return (
    <Box
      sx={{ alignItems: 'center', display: 'flex', gap: '4px', minWidth: 0 }}
    >
      <Typography color={color} noWrap variant='font12Medium'>
        <Box component='span' sx={{ fontFamily: 'monospace' }}>
          {value}
        </Box>
      </Typography>
      {/* Full hit area without adding row height. */}
      <Box sx={{ alignItems: 'center', display: 'flex', height: '18px' }}>
        <Tooltip
          tooltipContent={
            isCopied
              ? t('conversationAudit.copied')
              : t('conversationAudit.copy')
          }
        >
          <IconButton aria-label={ariaLabel} onClick={copy} variant='ghost'>
            {isCopied ? (
              <IconCheck color={palette.colors.green[500]} size={14} />
            ) : (
              <IconCopy color={palette.colors.grey[600]} size={14} />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
